import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  adminCancellationReasonOptions,
  onBehalfOfuserRoleOptions,
  TOnBehalfOfUserRoleOption,
} from '../../../../../constants';
import {
  cancelCoursZoomeSectionAsAdmin,
  ISectionForAdmin,
  TCancelCourseZoomSectionAsAdmin,
} from '../../../../../store/V2/SectionSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';

type ClassCancellationModalProps = {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  onSubmitted: (section: ISectionForAdmin) => void;
};

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  notifyUsers: boolean;
  selectedReason: TSelectOption | null;
  otherReason: string;
  onBehalfOfUserRole: TOnBehalfOfUserRoleOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const actionButtonStyles: SxProps = {
  borderRadius: '5px',
  padding: '16px 14px',
  fontWeight: 600,
  fontSize: '14px',
  maxHeight: '48px',
};

const initialState: TState = {
  notifyUsers: false,
  selectedReason: null,
  otherReason: '',
  onBehalfOfUserRole: null,
};

const ClassCancellationModal: React.FC<ClassCancellationModalProps> = ({
  open,
  onClose,
  sectionId,
  onSubmitted,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (state.selectedReason) {
      setLoading(true);

      const apiData: TCancelCourseZoomSectionAsAdmin = {
        _id: sectionId,
        reason: state.selectedReason.value,
        notifyUser: false,
      };

      if (state.selectedReason.value === 'Other') {
        apiData.reason = state.otherReason.trim();
      }

      if (state.notifyUsers) {
        apiData.notifyUser = state.notifyUsers;
      }

      if (state.onBehalfOfUserRole) {
        apiData.onBehalfOfUserRole = state.onBehalfOfUserRole.value;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await dispatch(cancelCoursZoomeSectionAsAdmin(apiData));

      if (res.payload) {
        onSubmitted(res.payload);
      }

      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: isXsScreen ? '15px' : '30px 30px 20px 30px',
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Cancel Class
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: isXsScreen ? '7px' : '22px',
          top: isXsScreen ? '7px' : '22px',
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: isXsScreen ? '0 15px' : '0 30px' }}>
        <div role="presentation">
          <Stack gap="20px">
            <div>
              <Typography color="#475569" fontSize="14px" fontWeight={400}>
                Are you sure? Do you want to cancel the class?
              </Typography>
            </div>
            <div>
              <FormControlLabel
                label="Notify Tutor and Student"
                control={
                  <Checkbox
                    checked={state.notifyUsers}
                    onChange={(e) =>
                      updateStateAttributes({ notifyUsers: e.target.checked })
                    }
                  />
                }
              />
            </div>
            <div>
              <Autocomplete
                fullWidth
                size="small"
                value={state.onBehalfOfUserRole}
                options={onBehalfOfuserRoleOptions}
                popupIcon={TPAutoCompleteArrowIconComponent}
                onChange={(e, value) =>
                  updateStateAttributes({ onBehalfOfUserRole: value })
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="On behalf of user role *"
                    placeholder="Select"
                  />
                )}
              />
            </div>
            <div>
              <Autocomplete
                fullWidth
                size="small"
                value={state.selectedReason}
                options={[
                  ...adminCancellationReasonOptions,
                  { label: 'Other', value: 'Other' },
                ]}
                onChange={(e, value) =>
                  updateStateAttributes({ selectedReason: value })
                }
                popupIcon={TPAutoCompleteArrowIconComponent}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Reason for cancelation *"
                    placeholder="Select"
                  />
                )}
              />
            </div>
            {state.selectedReason && state.selectedReason.value === 'Other' ? (
              <Collapse in>
                <div>
                  <TPTextField
                    {...textFieldProps}
                    fullWidth
                    size="small"
                    label="Other Reason"
                    placeholder="Enter here..."
                    value={state.otherReason}
                    onChange={(e) =>
                      updateStateAttributes({ otherReason: e.target.value })
                    }
                  />
                </div>
              </Collapse>
            ) : null}
            {/* <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing="10px"
            >
              <LoadingButton
                loading={isLoading}
                onClick={handleSubmit}
                disabled={
                  !state.selectedReason ||
                  (state.selectedReason &&
                    state.selectedReason.value === 'Other' &&
                    !state.otherReason.trim().length)
                }
                sx={{
                  ...actionButtonStyles,
                  color: '#FF5630',
                  backgroundColor: 'rgba(255, 86, 48, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 86, 48, 0.1)',
                  },
                }}
              >
                Cancel Class
              </LoadingButton>
            </Stack> */}
          </Stack>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: isXsScreen ? '15px' : '30px' }}>
        <LoadingButton
          loading={isLoading}
          onClick={handleSubmit}
          disabled={
            !state.selectedReason ||
            (state.selectedReason &&
              state.selectedReason.value === 'Other' &&
              !state.otherReason.trim().length)
          }
          sx={{
            ...actionButtonStyles,
            color: '#FF5630',
            backgroundColor: 'rgba(255, 86, 48, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 86, 48, 0.1)',
            },
          }}
        >
          Cancel Class
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ClassCancellationModal;
